import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AgreementForecast,
  ForecastPortalSettings,
  ProjectForecast,
  TicketForecast,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

const forecastKeys = {
  agreements: ['agreements', 'forecast'] as const,
  projects: ['projects', 'forecast'] as const,
  tickets: ['tickets', 'forecast'] as const,
  portal: ['forecast', 'portal'] as const,
};

export function useAgreementForecast(enabled = true): UseQueryResult<AgreementForecast> {
  return useQuery({
    queryKey: forecastKeys.agreements,
    queryFn: () => api.get<AgreementForecast>('/agreements/forecast'),
    staleTime: 60_000,
    enabled,
  });
}

export function useProjectForecast(enabled = true): UseQueryResult<ProjectForecast> {
  return useQuery({
    queryKey: forecastKeys.projects,
    queryFn: () => api.get<ProjectForecast>('/projects/forecast'),
    staleTime: 60_000,
    enabled,
  });
}

export function useTicketForecast(enabled = true): UseQueryResult<TicketForecast> {
  return useQuery({
    queryKey: forecastKeys.tickets,
    queryFn: () => api.get<TicketForecast>('/tickets/forecast'),
    staleTime: 60_000,
    enabled,
  });
}

/** Admin: the public forecast-portal settings (enabled + token). */
export function useForecastPortal(enabled = true): UseQueryResult<ForecastPortalSettings> {
  return useQuery({
    queryKey: forecastKeys.portal,
    queryFn: () => api.get<ForecastPortalSettings>('/forecast-portal'),
    enabled,
  });
}

/** Admin: generate or regenerate the public forecast link (mints a fresh token). */
export function useRegenerateForecastPortal(): UseMutationResult<
  ForecastPortalSettings,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ForecastPortalSettings>('/forecast-portal/regenerate'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: forecastKeys.portal });
    },
  });
}

/** Admin: disable the public forecast link (keeps the token, stops resolving). */
export function useDisableForecastPortal(): UseMutationResult<
  ForecastPortalSettings,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ForecastPortalSettings>('/forecast-portal/disable'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: forecastKeys.portal });
    },
  });
}
